import useLayoutStore from 'storages/layout';

import type { TourLayout, TourScenario, TourStep } from './types';

export const EXERCISES_TARGET = {
  main: 'exercises-main',
  filters: 'exercises-filters',
  filtersPrimary: 'exercises-filters-primary',
  filtersMore: 'exercises-filters-more',
  content: 'exercises-content',
  note: 'exercises-note',
  addToSet: 'exercises-add-to-set',
  navigation: 'exercises-navigation',
  navPrevNext: 'exercises-nav-prev-next',
  navFileType: 'exercises-nav-file-type',
  helpers: 'exercises-helpers',
  toolTables: 'exercises-tool-tables',
  toolNote: 'exercises-tool-note',
  toolDraw: 'exercises-tool-draw',
  toolPrint: 'exercises-tool-print',
  sheetsTimer: 'sheets-timer',
  sheetsFilters: 'sheets-filters',
  setsSelect: 'sets-select',
  setsActions: 'sets-actions',
} as const;

/** Sidebar tours are feature-specific; the task tour is shared across zadania / arkusze / zestawy. */
export const EXERCISES_TOUR_ID = {
  desktop: 'exercises-desktop',
  mobile: 'exercises-mobile',
} as const;

export const TASK_TOUR_ID = {
  desktop: 'task-desktop',
  mobile: 'task-mobile',
} as const;

export const SHEETS_TOUR_ID = {
  desktop: 'sheets-desktop',
  mobile: 'sheets-mobile',
} as const;

export const SETS_TOUR_ID = {
  desktop: 'sets-desktop',
  mobile: 'sets-mobile',
} as const;

export type WorkspaceTourFeature = 'exercises' | 'sheets' | 'sets';

export type ExercisesTargetId = typeof EXERCISES_TARGET[keyof typeof EXERCISES_TARGET];
export type WorkspaceTourId =
  | typeof EXERCISES_TOUR_ID[keyof typeof EXERCISES_TOUR_ID]
  | typeof TASK_TOUR_ID[keyof typeof TASK_TOUR_ID]
  | typeof SHEETS_TOUR_ID[keyof typeof SHEETS_TOUR_ID]
  | typeof SETS_TOUR_ID[keyof typeof SETS_TOUR_ID];

/** @deprecated Use WorkspaceTourId */
export type ExercisesTourId = WorkspaceTourId;

type ExercisesStep = Omit<TourStep, 'targetId'> & {
  targetId: ExercisesTargetId;
};

export type ExercisesScenario = Omit<TourScenario, 'id' | 'steps'> & {
  id: WorkspaceTourId;
  steps: ExercisesStep[];
};

const TABLE_SUBJECTS = ['CH', 'B', 'FI', 'M', 'MR'];

const openMobileFilters = (): void => {
  useLayoutStore.getState().openMobileFilterDrawer();
};

const closeMobileFilters = (): void => {
  useLayoutStore.getState().closeMobileFilterDrawer();
};

const sidebarDesktopSteps = (): ExercisesStep[] => [
  {
    targetId: EXERCISES_TARGET.filtersPrimary,
    title: 'Dział, poddział i sortowanie',
    description: 'Tu zmienisz dział, poddział i sortowanie.',
  },
  {
    targetId: EXERCISES_TARGET.filtersMore,
    title: 'Filtry',
    description: 'Tutaj możesz filtrować zadania na różne sposoby — po typie, trudności czy czasownikach operacyjnych.',
  },
];

const sidebarMobileSteps = (): ExercisesStep[] => [
  {
    targetId: EXERCISES_TARGET.filtersPrimary,
    title: 'Dział, poddział i sortowanie',
    description: 'Tu zmienisz dział, poddział i sortowanie.',
    prepare: openMobileFilters,
  },
  {
    targetId: EXERCISES_TARGET.filtersMore,
    title: 'Filtry',
    description: 'Tutaj możesz filtrować zadania na różne sposoby — po typie, trudności czy czasownikach operacyjnych.',
    prepare: openMobileFilters,
  },
];

const sheetsDesktopSteps = (): ExercisesStep[] => [
  {
    targetId: EXERCISES_TARGET.sheetsTimer,
    title: 'Ustaw czas timera',
    description:
      'Żeby zbliżyć odczucia do matury, możesz ustawić odliczanie czasu na arkusz — albo zostawić jak jest i nie odmierzać czasu.',
  },
  {
    targetId: EXERCISES_TARGET.sheetsFilters,
    title: 'Filtry',
    description: 'Wybierz, jaki arkusz chcesz, i zacznij pracę.',
  },
];

const sheetsMobileSteps = (): ExercisesStep[] => [
  {
    targetId: EXERCISES_TARGET.sheetsTimer,
    title: 'Ustaw czas timera',
    description:
      'Żeby zbliżyć odczucia do matury, możesz ustawić odliczanie czasu na arkusz — albo zostawić jak jest i nie odmierzać czasu.',
    prepare: openMobileFilters,
  },
  {
    targetId: EXERCISES_TARGET.sheetsFilters,
    title: 'Filtry',
    description: 'Wybierz, jaki arkusz chcesz, i zacznij pracę.',
    prepare: openMobileFilters,
  },
];

const setsDesktopSteps = (): ExercisesStep[] => [
  {
    targetId: EXERCISES_TARGET.setsSelect,
    title: 'Wybór zestawu',
    description: 'Tu wybierasz zestaw zadań. Nowe zestawy pojawią się na liście po utworzeniu.',
  },
  {
    targetId: EXERCISES_TARGET.setsActions,
    title: 'Dodaj lub edytuj zestaw',
    description:
      'Tu możesz dodać nowy zestaw albo edytować istniejący. Znacznie wygodniej jest dodawać zadania przez przycisk dodawania w widoku zadań lub arkuszy.',
  },
];

const setsMobileSteps = (): ExercisesStep[] => [
  {
    targetId: EXERCISES_TARGET.setsSelect,
    title: 'Wybór zestawu',
    description: 'Tu wybierasz zestaw zadań. Nowe zestawy pojawią się na liście po utworzeniu.',
    prepare: openMobileFilters,
  },
  {
    targetId: EXERCISES_TARGET.setsActions,
    title: 'Dodaj lub edytuj zestaw',
    description:
      'Tu możesz dodać nowy zestaw albo edytować istniejący. Znacznie wygodniej jest dodawać zadania przez przycisk dodawania w widoku zadań lub arkuszy.',
    prepare: openMobileFilters,
  },
];

type TaskScenarioOptions = {
  includeAddToSet?: boolean;
  includeTables?: boolean;
};

const taskDesktopSteps = ({ includeAddToSet = false, includeTables = true }: TaskScenarioOptions = {}): ExercisesStep[] => {
  const steps: ExercisesStep[] = [
    {
      targetId: EXERCISES_TARGET.navPrevNext,
      title: 'Wstecz / Dalej',
      description: 'Służy do przechodzenia do kolejnego lub poprzedniego zadania.',
    },
    {
      targetId: EXERCISES_TARGET.navFileType,
      title: 'Treść / Podpowiedź / Odpowiedź',
      description: 'Tu możesz podejrzeć odpowiedź do zadania, podpowiedź albo wrócić do treści.',
    },
    {
      targetId: EXERCISES_TARGET.content,
      title: 'Treść zadania',
      description: 'To jest główny widok zadania lub odpowiedzi.',
    },
    {
      targetId: EXERCISES_TARGET.note,
      title: 'Notatka',
      description: 'Tu możesz wpisać notatkę do zadania — będzie widoczna tylko dla Ciebie i zapisana przy tym zadaniu.',
    },
  ];

  if (includeAddToSet) {
    steps.push({
      targetId: EXERCISES_TARGET.addToSet,
      title: 'Dodaj zadanie do zestawu',
      description: 'Tu możesz dodać zadanie do zestawu i później do niego wrócić. Liczba na przycisku to numer zadania.',
    });
  }

  if (includeTables) {
    steps.push({
      targetId: EXERCISES_TARGET.toolTables,
      title: 'Narzędzia — tablice',
      description: 'Wyświetl tablice do przedmiotów ścisłych albo załączniki, takie jak mapy czy tabele.',
    });
  }

  steps.push(
    {
      targetId: EXERCISES_TARGET.toolNote,
      title: 'Narzędzia — notatka',
      description: 'Szybkie przejście do notatki przy zadaniu.',
    },
    {
      targetId: EXERCISES_TARGET.toolDraw,
      title: 'Rysowanie',
      description: 'Przejdź w tryb rysowania po zadaniu.',
    },
    {
      targetId: EXERCISES_TARGET.toolPrint,
      title: 'Drukowanie',
      description: 'Jeśli kolejka zadań jest mniejsza niż 50, możesz ją wydrukować. Drukowanie jest wyłączone, dopóki kolejka ma więcej niż 50 zadań.',
    },
  );

  return steps;
};

const taskMobileSteps = (
  subjectCode: string | null,
  { includeAddToSet = false }: TaskScenarioOptions = {},
): ExercisesStep[] => {
  const steps: ExercisesStep[] = [
    {
      targetId: EXERCISES_TARGET.navPrevNext,
      title: 'Wstecz / Dalej',
      description: 'Służy do przechodzenia do kolejnego lub poprzedniego zadania.',
      prepare: closeMobileFilters,
    },
    {
      targetId: EXERCISES_TARGET.navFileType,
      title: 'Treść / Odpowiedź',
      description: 'Tu możesz podejrzeć odpowiedź do zadania albo wrócić do treści.',
      prepare: closeMobileFilters,
    },
    {
      targetId: EXERCISES_TARGET.content,
      title: 'Treść zadania',
      description: 'To jest główny widok zadania lub odpowiedzi.',
      prepare: closeMobileFilters,
    },
    {
      targetId: EXERCISES_TARGET.note,
      title: 'Notatka',
      description: 'Tu możesz wpisać notatkę do zadania — będzie widoczna tylko dla Ciebie i zapisana przy tym zadaniu.',
      prepare: closeMobileFilters,
    },
  ];

  if (includeAddToSet) {
    steps.push({
      targetId: EXERCISES_TARGET.addToSet,
      title: 'Dodaj zadanie do zestawu',
      description: 'Tu możesz dodać zadanie do zestawu i później do niego wrócić. Liczba na przycisku to numer zadania.',
      prepare: closeMobileFilters,
    });
  }

  if (subjectHasMobileHelpers(subjectCode)) {
    steps.push({
      targetId: EXERCISES_TARGET.toolTables,
      title: 'Narzędzia — tablice',
      description: 'Wyświetl tablice do przedmiotów ścisłych albo załączniki, takie jak mapy czy tabele.',
      prepare: closeMobileFilters,
    });
  }

  return steps;
};

export const subjectHasMobileHelpers = (subjectCode: string | null): boolean => {
  if (!subjectCode) {
    return false;
  }

  if (subjectCode === 'GE') {
    return true;
  }

  return TABLE_SUBJECTS.includes(subjectCode);
};

export const subjectHasDesktopTables = (subjectCode: string | null): boolean => subjectHasMobileHelpers(subjectCode);

export const taskTourIdOf = (layout: TourLayout): typeof TASK_TOUR_ID[keyof typeof TASK_TOUR_ID] =>
  layout === 'desktop' ? TASK_TOUR_ID.desktop : TASK_TOUR_ID.mobile;

export const sidebarTourIdOf = (
  feature: WorkspaceTourFeature,
  layout: TourLayout,
): WorkspaceTourId | null => {
  if (feature === 'exercises') {
    return layout === 'desktop' ? EXERCISES_TOUR_ID.desktop : EXERCISES_TOUR_ID.mobile;
  }

  if (feature === 'sheets') {
    return layout === 'desktop' ? SHEETS_TOUR_ID.desktop : SHEETS_TOUR_ID.mobile;
  }

  if (feature === 'sets') {
    return layout === 'desktop' ? SETS_TOUR_ID.desktop : SETS_TOUR_ID.mobile;
  }

  return null;
};

export const buildSidebarScenario = (layout: TourLayout): ExercisesScenario => {
  if (layout === 'desktop') {
    return { id: EXERCISES_TOUR_ID.desktop, steps: sidebarDesktopSteps() };
  }

  return { id: EXERCISES_TOUR_ID.mobile, steps: sidebarMobileSteps() };
};

export const buildSheetsSidebarScenario = (layout: TourLayout): ExercisesScenario => {
  if (layout === 'desktop') {
    return { id: SHEETS_TOUR_ID.desktop, steps: sheetsDesktopSteps() };
  }

  return { id: SHEETS_TOUR_ID.mobile, steps: sheetsMobileSteps() };
};

export const buildSetsSidebarScenario = (layout: TourLayout): ExercisesScenario => {
  if (layout === 'desktop') {
    return { id: SETS_TOUR_ID.desktop, steps: setsDesktopSteps() };
  }

  return { id: SETS_TOUR_ID.mobile, steps: setsMobileSteps() };
};

export const buildFeatureSidebarScenario = (
  feature: WorkspaceTourFeature,
  layout: TourLayout,
): ExercisesScenario | null => {
  if (feature === 'exercises') {
    return buildSidebarScenario(layout);
  }

  if (feature === 'sheets') {
    return buildSheetsSidebarScenario(layout);
  }

  if (feature === 'sets') {
    return buildSetsSidebarScenario(layout);
  }

  return null;
};

export const buildTaskScenario = (
  layout: TourLayout,
  subjectCode: string | null,
  options: TaskScenarioOptions = {},
): ExercisesScenario => {
  if (layout === 'desktop') {
    return {
      id: TASK_TOUR_ID.desktop,
      steps: taskDesktopSteps({
        includeAddToSet: options.includeAddToSet,
        includeTables: options.includeTables ?? subjectHasDesktopTables(subjectCode),
      }),
    };
  }

  return {
    id: TASK_TOUR_ID.mobile,
    steps: taskMobileSteps(subjectCode, { includeAddToSet: options.includeAddToSet }),
  };
};

type ResolveNextWorkspaceScenarioInput = {
  feature: WorkspaceTourFeature;
  layout: TourLayout;
  hasTask: boolean;
  subjectCode: string | null;
  seenGuides: readonly string[];
  includeAddToSet?: boolean;
};

type ResolveWorkspaceTourQueueInput = ResolveNextWorkspaceScenarioInput & {
  /** When true, include tours even if already marked seen (replay). */
  force?: boolean;
};

/**
 * Pending workspace tours in order: feature sidebar first, then the shared task tour
 * once a task is visible. Used so sidebar + task can run as one merged session.
 */
export const resolveWorkspaceTourQueue = ({
  feature,
  layout,
  hasTask,
  subjectCode,
  seenGuides,
  includeAddToSet = false,
  force = false,
}: ResolveWorkspaceTourQueueInput): ExercisesScenario[] => {
  const queue: ExercisesScenario[] = [];
  const sidebar = buildFeatureSidebarScenario(feature, layout);

  if (sidebar && (force || !seenGuides.includes(sidebar.id))) {
    queue.push(sidebar);
  }

  if (hasTask) {
    const taskId = taskTourIdOf(layout);

    if (force || !seenGuides.includes(taskId)) {
      queue.push(buildTaskScenario(layout, subjectCode, { includeAddToSet }));
    }
  }

  return queue;
};

/**
 * Feature sidebar first (when defined); shared task tour after that, once a task is visible.
 * Task guide ids are shared across zadania / arkusze / zestawy.
 */
export const resolveNextWorkspaceScenario = (
  input: ResolveNextWorkspaceScenarioInput,
): ExercisesScenario | null => resolveWorkspaceTourQueue(input)[0] ?? null;

/** @deprecated Use resolveNextWorkspaceScenario */
export const resolveNextExercisesScenario = (
  input: Omit<ResolveNextWorkspaceScenarioInput, 'feature'>,
): ExercisesScenario | null => resolveNextWorkspaceScenario({ ...input, feature: 'exercises' });

export const isSidebarTourId = (scenarioId: string): boolean =>
  scenarioId === EXERCISES_TOUR_ID.desktop ||
  scenarioId === EXERCISES_TOUR_ID.mobile ||
  scenarioId === SHEETS_TOUR_ID.desktop ||
  scenarioId === SHEETS_TOUR_ID.mobile ||
  scenarioId === SETS_TOUR_ID.desktop ||
  scenarioId === SETS_TOUR_ID.mobile;
